import {
  ApiError,
  DeltaAction,
  ErrorCode,
  FileRefusalReason,
  RowOutcome,
  type SpreadsheetUploadCompany,
  type SpreadsheetUploadContract,
  type SpreadsheetUploadRowResponse,
} from '../../api';
import { formatCpf, formatCurrency, formatDate, formatInteger } from '../../format';
import { identityMismatchMessage } from './identityMismatch';

/**
 * What the upload screen says, and how it turns a rejected row into a line a
 * person can act on.
 *
 * The server's `rejection_reason` is written for an API caller and is in
 * English. `rejection_code` is the stable half of the same answer, so the pt-BR
 * sentence is chosen from it here -- the same discipline the error envelope
 * follows in `src/api/errors.ts`, for the same reason: prose is made clearer
 * over time and a UI that matched on it would break when it was.
 *
 * A code we do not know yet is not a blank line. It falls back to a sentence
 * that says the row was refused and tells the person where the detail is, which
 * is worse than a translation and much better than nothing.
 *
 * One code needs more than a fixed sentence. `identity_mismatch` is the only
 * refusal whose fix is outside the sheet -- the Receita Federal disagrees with
 * the name or the birth date -- and the useful sentence names what the registry
 * actually holds. That value arrives as structured data on `rejection_context`,
 * keyed by field, for the same reason the code does: `rejection_reason` says
 * the same thing in English and must never be parsed.
 *
 * `unmarked_salary_differs` is the other: a row with no movement marked whose
 * salary is not the one on record. The useful sentence says which salary is on
 * record, and that arrives on `rejection_context.salary` -- never in the
 * reason, which is stored in the clear and carries no amount.
 */

export const UPLOAD_COPY = {
  title: 'Envio de planilha',
  description:
    'Envie a planilha de movimentações de todos os contratos da empresa. Cada linha vai para o contrato do seu subestipulante e módulo. Nada é gravado até você conferir a prévia e confirmar.',
  templateTitle: 'Modelo vylife',
  templateDescription:
    'A mesma planilha de sempre, com uma coluna de data de vigência por linha. Toda movimentação precisa de uma data, e a planilha antiga não tem onde escrevê-la.',
  downloadXlsx: 'Baixar modelo (.xlsx)',
  downloadCsv: 'Baixar modelo (.csv)',
  formTitle: 'Enviar planilha',
  fileLabel: 'Planilha preenchida',
  fileHint: 'Arquivo .xlsx ou .csv, no modelo vylife ou no formato antigo.',
  effectiveDateLabel: 'Data de vigência das movimentações',
  effectiveDateHint:
    'Usada nas linhas que não trazem data própria. Uma linha com data na planilha usa a dela.',
  submit: 'Enviar e conferir',
  previewTitle: 'Prévia',
  previewDescription: 'Confira antes de confirmar. Até aqui nada foi gravado.',
  confirm: 'Confirmar envio',
  confirming: 'Confirmando…',
  onlyRejected: 'Mostrar somente as linhas recusadas',
  exportRejected: 'Exportar linhas recusadas (.csv)',
  committed: 'Envio confirmado.',
  duplicate: 'Esta planilha já tinha sido confirmada para esta empresa. Nada foi enviado de novo.',
  contractsTitle: 'Resumo por contrato',
  contractsCaption: 'Linhas da planilha por contrato',
  noContractRouted: 'Sem contrato',
  nothingToCommit: 'Nenhuma linha pronta para enviar.',
  noContract: 'Sua empresa ainda não tem um contrato cadastrado. Fale com a vyttech para começar.',
  absentTitle: 'Pessoas seguradas que não estão na planilha',
  absentDescription:
    'Estas pessoas estão seguradas, mas não aparecem em nenhuma linha da planilha. Elas continuam seguradas, a menos que você envie uma exclusão para cada uma.',
  absentCaption: 'Pessoas seguradas fora da planilha',
  targetLegend: 'Para quem é esta planilha',
  targetGroup: (group: string) => `Todo o grupo (${group})`,
  targetGroupHint:
    'Uma planilha com as pessoas de todas as empresas do grupo. Cada linha vai para a empresa e o contrato do seu subestipulante e módulo.',
  targetCompany: (company: string) => `Somente a empresa selecionada (${company})`,
  targetCompanyHint: 'Uma planilha só com as pessoas desta empresa, como antes.',
  groupDuplicate:
    'Esta planilha já tinha sido confirmada para este grupo. Nada foi enviado de novo.',
  companiesTitle: 'Resumo por empresa',
  companiesCaption: 'Linhas da planilha por empresa',
  noCompanyRouted: 'Sem empresa',
} as const;

/**
 * What the screen says when the whole file is refused, per `details[].reason`.
 *
 * Kept beside {@link UPLOAD_COPY} because it is the same screen's voice. Three
 * reasons are composed from values rather than fixed: the columns a sheet
 * lacks, and the two ceilings.
 */
export const FILE_REFUSAL_COPY = {
  [FileRefusalReason.EMPTY_FILE]: 'O arquivo está vazio.',
  [FileRefusalReason.UNREADABLE_FORMAT]: 'Não foi possível ler o arquivo. Envie um .xlsx ou .csv.',
  [FileRefusalReason.LEGACY_XLS]:
    'Este é um arquivo .xls antigo, que não conseguimos ler. Abra no Excel e salve como .xlsx ou .csv.',
  [FileRefusalReason.NO_HEADER]:
    'A primeira linha da planilha está em branco. Ela precisa trazer os nomes das colunas, como no modelo acima.',
  [FileRefusalReason.NO_ROWS]:
    'A planilha tem cabeçalho mas nenhuma linha. Preencha ao menos uma linha e envie de novo.',
  tooManyRows: (limit: number) =>
    `A planilha tem mais de ${formatInteger(limit)} linhas. Divida em arquivos menores e envie cada um.`,
  tooManyRowsWithoutLimit:
    'A planilha tem linhas demais. Divida em arquivos menores e envie cada um.',
  fileTooLarge: (megabytes: number) =>
    `O arquivo tem mais de ${formatInteger(megabytes)} MB. Envie uma versão menor, ou divida em arquivos menores.`,
  fileTooLargeWithoutLimit: 'Este arquivo é grande demais. Envie uma versão menor.',
  missingColumns: (columns: readonly string[]) =>
    `${columns.length > 1 ? 'A planilha não tem as colunas' : 'A planilha não tem a coluna'} ${joined(columns)}. Acentos e maiúsculas não importam, mas as colunas precisam existir — baixe o modelo acima para conferir.`,
} as const;

const BYTES_PER_MEGABYTE = 1024 * 1024;

function joined(parts: readonly string[]): string {
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

/**
 * Why the upload refused the whole file, as a pt-BR sentence -- or `undefined`
 * when the error is not such a refusal, so the caller keeps its general one.
 *
 * Composed from `details[].reason`, `column` and `limit` only. The envelope's
 * `message` says the same thing in English for an API caller and is never read.
 */
export function fileRefusalMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || error.code !== ErrorCode.VALIDATION_ERROR) {
    return undefined;
  }
  const refusals = error.details.filter((detail) => detail.reason);
  const reason = refusals[0]?.reason;
  if (reason === undefined || reason === null) {
    return undefined;
  }
  const limit = refusals[0]?.limit ?? undefined;
  switch (reason) {
    case FileRefusalReason.MISSING_COLUMNS: {
      const columns = refusals
        .filter((detail) => detail.reason === reason)
        .map((detail) => detail.column)
        .filter((column): column is string => typeof column === 'string' && column !== '');
      return columns.length > 0 ? FILE_REFUSAL_COPY.missingColumns(columns) : undefined;
    }
    case FileRefusalReason.TOO_MANY_ROWS:
      return limit === undefined
        ? FILE_REFUSAL_COPY.tooManyRowsWithoutLimit
        : FILE_REFUSAL_COPY.tooManyRows(limit);
    case FileRefusalReason.FILE_TOO_LARGE:
      return limit === undefined
        ? FILE_REFUSAL_COPY.fileTooLargeWithoutLimit
        : FILE_REFUSAL_COPY.fileTooLarge(Math.floor(limit / BYTES_PER_MEGABYTE));
    case FileRefusalReason.EMPTY_FILE:
    case FileRefusalReason.UNREADABLE_FORMAT:
    case FileRefusalReason.LEGACY_XLS:
    case FileRefusalReason.NO_HEADER:
    case FileRefusalReason.NO_ROWS:
      return FILE_REFUSAL_COPY[reason];
    default:
      // A reason added to the contract after this screen was written.
      return undefined;
  }
}

export const OUTCOME_LABELS: Record<string, string> = {
  [RowOutcome.READY]: 'Pronta',
  [RowOutcome.COMMITTED]: 'Enviada',
  [RowOutcome.IGNORED]: 'Sem movimentação',
  [RowOutcome.REJECTED]: 'Recusada',
  [RowOutcome.SECTION_TITLE]: 'Título de seção',
};

export const ACTION_LABELS: Record<string, string> = {
  [DeltaAction.ADMISSION]: 'Inclusão',
  [DeltaAction.TERMINATION]: 'Exclusão',
  [DeltaAction.SALARY_CHANGE]: 'Alteração salarial',
};

/** One sentence per `rejection_code`, saying what to do rather than what failed. */
const REJECTION_MESSAGES: Record<string, string> = {
  multiple_actions:
    'Mais de uma coluna de movimentação está marcada. Marque só uma, ou separe em uma linha para cada.',
  contract_mismatch:
    'O subestipulante ou o módulo da linha não correspondem a nenhum contrato da empresa. Esses códigos são do contrato, não da pessoa.',
  ambiguous_contract:
    'A linha serve para mais de um contrato da empresa. Preencha o subestipulante e o módulo para indicar qual.',
  invalid_cpf: 'O CPF não confere. Verifique os dígitos verificadores.',
  missing_effective_date:
    'A movimentação está sem data de vigência. Preencha a coluna de data ou informe a data no envio.',
  missing_value: 'Falta preencher um campo que esta movimentação exige.',
  invalid_value: 'Um campo desta linha não está no formato esperado.',
  duplicate_row: 'A mesma movimentação para esta pessoa já aparece antes na planilha.',
  roster_conflict:
    'A movimentação não cabe no cadastro nesta data: confira se a pessoa já está (ou ainda não está) coberta.',
  unmarked_not_on_roster:
    'Nenhuma movimentação está marcada, mas esta pessoa não está segurada neste contrato na data de conferência. Se é uma inclusão, marque a coluna INCLUSAO.',
  not_well_formed: 'A linha não descreve uma movimentação válida.',
};

/**
 * The two routing refusals, worded for a sheet sent for the whole group: the
 * codes a row carries are checked against every company of the group, not one.
 */
const GROUP_REJECTION_MESSAGES: Record<string, string> = {
  contract_mismatch:
    'O subestipulante ou o módulo da linha não correspondem a nenhum contrato das empresas do grupo. Esses códigos são do contrato, não da pessoa.',
  ambiguous_contract:
    'A linha serve para mais de um contrato das empresas do grupo. Preencha o subestipulante e o módulo para indicar qual.',
};

/** Whether a sheet was sent for one company or for the whole group. */
export type UploadTarget = 'company' | 'group';

/**
 * The one code with no fixed sentence: what it says depends on the values the
 * row came back with. Absent from {@link REJECTION_MESSAGES} on purpose, so
 * there is no wording for it to fall through to.
 */
const IDENTITY_MISMATCH = 'identity_mismatch';

/** Also composed from values: the salary on record, when there is one. */
const UNMARKED_SALARY_DIFFERS = 'unmarked_salary_differs';

/** A row with no movement marked whose salary is not the one on record. */
export function unmarkedSalaryMessage(context: Readonly<Record<string, string>>): string {
  const onRecord = context.salary;
  const what =
    onRecord === undefined
      ? 'não há salário cadastrado para esta pessoa neste contrato na data de conferência'
      : `o salário da planilha é diferente do salário cadastrado, de ${formatCurrency(onRecord)}`;
  return `Nenhuma movimentação está marcada, mas ${what}. Se o salário mudou, marque a coluna ALTERAÇÃO SALARIO.`;
}

export function messageForRejection(
  row: SpreadsheetUploadRowResponse,
  target: UploadTarget = 'company',
): string {
  if (row.rejection_code === null || row.rejection_code === undefined) {
    return '';
  }
  if (row.rejection_code === IDENTITY_MISMATCH) {
    // An empty context is a CPF the registry has never issued: it reports
    // `rejection_field` as the CPF and holds no value to quote.
    return identityMismatchMessage(row.rejection_context ?? {});
  }
  if (row.rejection_code === UNMARKED_SALARY_DIFFERS) {
    return unmarkedSalaryMessage(row.rejection_context ?? {});
  }
  return (
    (target === 'group' ? GROUP_REJECTION_MESSAGES[row.rejection_code] : undefined) ??
    REJECTION_MESSAGES[row.rejection_code] ??
    'Esta linha foi recusada. Exporte as linhas recusadas para ver o detalhe.'
  );
}

/**
 * Which company of the group a row, an absence or a count went to. A line whose
 * codes chose no contract of the group belongs to no company.
 */
export function companyLabel(company: SpreadsheetUploadCompany | null | undefined): string {
  return company === null || company === undefined ? UPLOAD_COPY.noCompanyRouted : company.name;
}

/** Which contract a row went to, by the codes that chose it. */
export function contractLabel(contract: SpreadsheetUploadContract | null | undefined): string {
  if (contract === null || contract === undefined) {
    return UPLOAD_COPY.noContractRouted;
  }
  return `Subestipulante ${contract.subestipulante} · Módulo ${contract.modulo}`;
}

export function isRejected(row: SpreadsheetUploadRowResponse): boolean {
  return row.outcome === RowOutcome.REJECTED;
}

/**
 * The rejected rows as a spreadsheet the customer can open, fix and send back.
 *
 * Semicolons and UTF-8 with a byte-order mark, which is what Excel on a
 * Brazilian machine opens without being asked. Every field is quoted and inner
 * quotes are doubled, so a name with a comma in it stays one cell -- and a cell
 * that begins with `=`, `+`, `-` or `@` is prefixed with an apostrophe, because
 * a spreadsheet would otherwise treat what we exported as a formula to run.
 */
const CSV_HEADER = [
  'Linha',
  'Contrato',
  'Movimentação',
  'CPF',
  'Nome',
  'Vigência',
  'Salário',
  'Motivo',
];

function cell(value: string): string {
  const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${guarded.replace(/"/g, '""')}"`;
}

export function rejectedRowsCsv(
  rows: readonly (SpreadsheetUploadRowResponse & { company?: SpreadsheetUploadCompany | null })[],
  target: UploadTarget = 'company',
): string {
  // A group's sheet names the company of each row too, beside its contract.
  const header = target === 'group' ? ['Linha', 'Empresa', ...CSV_HEADER.slice(1)] : CSV_HEADER;
  const lines = [header.map(cell).join(';')];
  for (const row of rows.filter(isRejected)) {
    lines.push(
      [
        String(row.row_number),
        ...(target === 'group' ? [companyLabel(row.company)] : []),
        contractLabel(row.contract),
        row.action === null || row.action === undefined ? '' : (ACTION_LABELS[row.action] ?? ''),
        row.cpf === null || row.cpf === undefined ? '' : formatCpf(row.cpf),
        row.full_name ?? '',
        row.effective_date === null || row.effective_date === undefined
          ? ''
          : formatDate(row.effective_date),
        row.salary === null || row.salary === undefined ? '' : formatCurrency(row.salary),
        messageForRejection(row, target),
      ]
        .map(cell)
        .join(';'),
    );
  }
  // The BOM is added when the blob is built, not here: this function returns
  // text, and a caller that puts it on screen should not be shown a stray glyph.
  return lines.join('\r\n');
}

/** What the exported file is called. No identifier of anybody in the name. */
export function rejectedRowsFilename(): string {
  return 'linhas-recusadas.csv';
}
